import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Template } from 'tabler-icons-react';
import dayjs from 'dayjs';
import { Context as RegistrationAdminContext } from '../../../../providers/RegistrationAdminProvider';
import { useRegAssociationAdminPermissions } from '../../../../helpers/adminHelper';
import { EVALUATION_VIEW_TABS } from '../navigation/links';
import AdminView from '../../../../components/common/AdminView';
import { useModalState, usePaginationFilter } from '../../../../helpers/hooks';
import TableView from '../../../../components/common/TableView';
import AppCard from '../../../../components/common/AppCard';
import { triggerNotification } from '../../../../helpers/notificationHelper';
import { formatUtcDate } from '../../../../helpers/format';
import CreateEvaluationTemplateModal from '../../../../components/content/evaluation/CreateEvaluationTemplateModal';

const VIEW_ACTIONS_ENUM = {
  create: 'CREATE',
  edit: 'EDIT',
  copy: 'COPY',
  delete: 'DELETE'
};

const VIEW_ACTIONS = [
  {
    label: 'Edit',
    value: VIEW_ACTIONS_ENUM.edit
  },
  {
    label: 'Copy',
    value: VIEW_ACTIONS_ENUM.copy
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.delete
  }
];

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Sessions',
    value: 'sessions',
    sortable: true
  },
  {
    label: 'Metrics',
    value: 'metrics',
    sortable: true
  },
  {
    label: 'Attributes',
    value: 'attributes',
    sortable: true
  },
  {
    label: 'Created',
    value: 'created',
    sortable: true
  }
];

const EvaluationTemplateTableView = () => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const {
    state,
    fetchPaginatedRegScoutReportTemplates,
    fetchRegScoutReportEvaluationOptions
  } = useContext(RegistrationAdminContext);
  const { regAssociation } = useRegAssociationAdminPermissions();
  const {
    pageIndex,
    pageSize,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      status: ''
    },
    (newFilter) => {
      fetchPaginatedRegScoutReportTemplates(
        regAssociation.pkRegAssociation,
        newFilter,
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedRegScoutReportTemplates
  );
  const loading = !hasFetched.current || dataLoading;
  const noDataCreated = !loading && totalNoFilterCount === 0;

  useEffect(() => {
    if (regAssociation) {
      fetchPaginatedRegScoutReportTemplates(
        regAssociation.pkRegAssociation,
        {},
        null,
        (error) => {
          triggerNotification(error);
        }
      );
      hasFetched.current = true;
    }
  }, [regAssociation]);

  return (
    <AdminView
      actionControl={{
        label: 'Create Template',
        icon: <Plus />,
        action: 'CREATE'
      }}
      headerTitle="Scouting & Evaluations"
      onAction={onOpenModal}
      tabs={EVALUATION_VIEW_TABS}
    >
      <AppCard style={{ flex: 1, padding: 0, height: '100%' }}>
        <TableView
          columns={TABLE_COLUMNS}
          disabled={noDataCreated}
          disabledContent={{
            title: 'No templates created',
            description: 'Start by your first template',
            icon: <Template color="#000" size={125} />,
            primaryButton: {
              label: 'Create Template',
              icon: <Plus size={18} />,
              onClick: () => onOpenModal(VIEW_ACTIONS_ENUM.create)
            }
          }}
          emptyMessage="No templates found."
          enableInputsWhenLoading
          filters={[
            {
              key: 1,
              label: 'Template Name',
              type: 'text',
              value: filter.search,
              placeholder: 'Filter by template',
              onChange: (value) => onFilterChange({ ...filter, search: value })
            }
          ]}
          isDescendingSort={isDescendingSort}
          isLoading={loading}
          noSort
          onAction={(action, item) => {
            if (action === VIEW_ACTIONS_ENUM.edit) {
              navigate(`/admin/evaluations/templates/${item.key}`);
            }
            else {
              onOpenModal(
                action,
                data.find((s) => s.pkRegScoutReport === item.key)
              );
            }
          }}
          onChangeSortBy={onSortChange}
          onPageChange={onPageChange}
          onRefresh={onRefresh}
          pageIndex={pageIndex}
          rows={data.map((s) => ({
            key: s.pkRegScoutReport,
            actions: VIEW_ACTIONS,
            columns: [
              {
                key: 1,
                label: s.name,
                weight: 500,
                icon: <Template color="#000" size="30" />,
                onClick: () =>
                  navigate(
                    `/admin/evaluations/templates/${s.pkRegScoutReport}`
                  ),
                showOnMobile: true
              },
              {
                key: 2,
                label: s.sessionCount
              },
              {
                key: 3,
                label: s.metricCount
              },
              {
                key: 4,
                label: s.attributeCount
              },
              {
                key: 5,
                label: dayjs(formatUtcDate(s.createdAt)).format('MMMM D, YYYY'),
                showOnMobile: true
              }
            ]
          }))}
          sortBy={filter.sort}
          tableTitle="Evaluation Templates"
          totalCount={totalCount}
          totalPages={totalPages}
        />

        <CreateEvaluationTemplateModal
          isOpen={modalState.isOpen}
          onClose={onCloseModal}
          onDelete={() => {
            onRefresh();
          }}
          pkRegAssociation={regAssociation?.pkRegAssociation}
          regScoutReport={modalState.item}
          showDelete={modalState.action === VIEW_ACTIONS_ENUM.delete}
        />
      </AppCard>
    </AdminView>
  );
};

export default EvaluationTemplateTableView;
